import { useScrollContext } from "@pathwright/ui/src/components/scroll/ScrollView"
import { useEffect } from "react"

const injectTemporaryStyles = () => {
  const existingStyle = document.getElementById("smooth-scroll-temp-style")

  if (existingStyle) {
    return () => {}
  }

  const style = document.createElement("style")
  style.id = "smooth-scroll-temp-style"
  style.innerHTML = `
    .smooth-scroll-active::after {
      content: "";
      position: absolute;
      opacity: 0.5;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      animation: highlight 2.5s ease-out;
    }
  `
  document.head.appendChild(style)

  return () => {
    document.head.removeChild(style)
  }
}

const highlightElement = (element: HTMLElement) => {
  element.classList.add("smooth-scroll-active")

  const handleBlur = () => {
    element.classList.remove("smooth-scroll-active")
  }

  element.addEventListener("blur", handleBlur)

  return () => {
    element.removeEventListener("blur", handleBlur)
  }
}

const scrollSmoothlyToElement = ({
  element,
  duration,
  scrollTo,
  callback,
  offset = 0
}: {
  element: HTMLElement
  duration: number
  scrollTo: (x: number, y: number) => void
  callback?: () => void
  offset?: number
}) => {
  const targetPosition =
    element.getBoundingClientRect().top + window.pageYOffset
  const startPosition = window.pageYOffset
  const distance = targetPosition - startPosition - offset
  const startTime = performance.now()

  const animation = (currentTime: number) => {
    const elapsedTime = currentTime - startTime
    const progress = Math.min(elapsedTime / duration, 1)
    const ease =
      progress < 0.5
        ? 2 * progress * progress
        : -1 + (4 - 2 * progress) * progress

    scrollTo(startPosition + distance * ease, 0)

    if (progress < 1) {
      requestAnimationFrame(animation)
    } else if (callback) {
      callback()
    }
  }

  requestAnimationFrame(animation)
}

const scrollToAndHighlight = ({
  elementID,
  scrollTo,
  duration,
  offset,
  autofocus
}: {
  elementID: string
  scrollTo: (x: number, y: number) => void
  duration: number
  offset?: number
  autofocus?: boolean
}) => {
  const element = document.getElementById(elementID)

  if (!element || !scrollTo) return

  const cleanup = highlightElement(element)

  scrollSmoothlyToElement({
    element,
    duration,
    scrollTo,
    callback: () => {
      if (autofocus) {
        element.setAttribute("tabindex", "-1")
        element.focus()
      }
      cleanup()
    },
    offset
  })
}

type UseScrollToAndHighlightProps = {
  onScrollStart?: () => void
  onScrollEnd?: () => void
  isActive?: boolean
  scrollDuration?: number
  offset?: number
  autofocus?: boolean
}

const useScrollToAndHighlight = ({
  onScrollStart = () => {},
  onScrollEnd = () => {},
  isActive = true,
  scrollDuration = 500,
  offset = 0,
  autofocus = true
}: UseScrollToAndHighlightProps = {}) => {
  const scrollContext = useScrollContext() || {}
  const { scrollTo } = scrollContext

  useEffect(() => {
    // Inject temporary styles for highlighting
    const removeStyles = injectTemporaryStyles()

    // Store hash on mount, as we only want to handle scrolling to
    // element on mount. Otherwise, hash may be updated between mount
    // and the timeout end.
    const hash = window.location.hash

    // Give the page a bit of extra time to finish rendering
    setTimeout(() => {
      if (hash) {
        const id = hash.replace("#", "")
        const element = document.getElementById(id)
        if (element) {
          // Disable observer during smooth scroll
          onScrollStart()

          // Smooth scroll implementation
          if (isActive && scrollTo) {
            scrollToAndHighlight({
              elementID: id,
              scrollTo,
              duration: scrollDuration,
              offset,
              autofocus
            })
          }
        }
      }

      // Always handle scroll end, whether scroll happened or not
      setTimeout(() => {
        onScrollEnd()
      }, scrollDuration + 50)
    }, 250)

    return () => {
      removeStyles()
    }
  }, [])
}

export default useScrollToAndHighlight
